import { motion } from "framer-motion";
import { Activity, Cpu, HardDrive, Server, ShieldCheck, Sparkles, Database, Zap, FileCheck2 } from "lucide-react";

const NODE_TYPES = [
    { name: "Light Node", icon: Activity, stake: "1,000 DCX", role: "Bandwidth · Relay" },
    { name: "Standard Node", icon: Server, stake: "10,000 DCX", role: "Compute · Routing" },
    { name: "GPU Node", icon: Cpu, stake: "25,000 DCX", role: "AI Inference · Training" },
    { name: "Storage Node", icon: HardDrive, stake: "15,000 DCX", role: "Encrypted Storage" },
    { name: "Validator Node", icon: ShieldCheck, stake: "100,000 DCX", role: "Block Production" },
    { name: "Enterprise Verified", icon: Sparkles, stake: "Tier-1", role: "SLA-Backed Capacity" },
];

const DASHBOARD = {
    nodeId: "DCX-7A3F-2E11-NLD",
    region: "EU-WEST-2 · Amsterdam",
    uptime: "99.982%",
    earnings: "12,847.32",
    earnings24h: "+184.21",
    compute: "82%",
    storage: "1.42 TiB",
    aiJobs: "1,284",
    reliability: "98",
    stake: "25,000",
    status: "ACTIVE",
    proofStatus: "Verified · 2m ago",
};

const WORKLOAD_HISTORY = [
    { t: "00:14:02", type: "AI Inference", model: "llama-3.1-70b", units: "412 tk/s", earn: "+0.84" },
    { t: "00:13:45", type: "Storage Proof", model: "shard-9af2", units: "16/16", earn: "+0.12" },
    { t: "00:13:21", type: "Compute Job", model: "container", units: "4 vCPU · 8 GiB", earn: "+0.31" },
    { t: "00:12:58", type: "GPU Train", model: "fine-tune", units: "1× H100 · 6m", earn: "+1.62" },
    { t: "00:12:30", type: "DNS Resolve", model: "*.dcx", units: "1,204 req", earn: "+0.04" },
];

export default function NodeEconomy() {
    return (
        <section id="nodes" className="relative py-24 md:py-32 lg:py-40 border-t border-line" data-testid="node-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-3xl mb-16">
                    <div className="overline mb-4">04 — DePIN Node Economy</div>
                    <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.06] tracking-tight text-ink">
                        Own the Cloud.<br />
                        Run a Node. <span className="brand-gradient-text">Earn From Infrastructure.</span>
                    </h2>
                    <p className="mt-6 text-base text-muted-ink max-w-2xl leading-relaxed">
                        DeCloudX transforms distributed hardware into a programmable cloud network. Individuals, data centers, GPU providers, storage operators, enterprises, and validators can contribute infrastructure and earn from real network usage.
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
                    {NODE_TYPES.map((n, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.04 }}
                            className="surface card-lift p-5 rounded-xl"
                            data-testid={`node-type-${i}`}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <n.icon size={17} className="text-brand-blue" strokeWidth={1.5} />
                                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-faint-ink">TIER {i + 1}</span>
                            </div>
                            <div className="font-heading font-bold text-sm text-ink">{n.name}</div>
                            <div className="font-mono text-[10px] text-faint-ink mt-1.5 uppercase tracking-wider">{n.role}</div>
                            <div className="mt-3 pt-3 border-t border-line flex items-center justify-between">
                                <span className="font-mono text-[9px] text-faint-ink uppercase tracking-wider">Stake</span>
                                <span className="font-mono text-[11px] text-brand-blue">{n.stake}</span>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="surface-strong rounded-2xl overflow-hidden"
                    data-testid="node-dashboard"
                >
                    <div className="flex flex-wrap items-center justify-between gap-4 px-6 md:px-8 py-5 border-b border-line bg-card-muted">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-ink">
                                DeCloudX Operator Console · {DASHBOARD.region}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">{DASHBOARD.status}</span>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-12">
                        <div className="lg:col-span-7 p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-line">
                            <div className="flex items-baseline justify-between mb-5">
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">Node ID</div>
                                    <div className="font-mono text-sm text-ink mt-0.5">{DASHBOARD.nodeId}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">Uptime · 30d</div>
                                    <div className="font-mono text-base text-brand-blue mt-0.5">{DASHBOARD.uptime}</div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                                <Stat label="Compute" value={DASHBOARD.compute} icon={Cpu} accent="blue" />
                                <Stat label="Storage" value={DASHBOARD.storage} icon={Database} />
                                <Stat label="AI Jobs · 24h" value={DASHBOARD.aiJobs} icon={Zap} accent="violet" />
                                <Stat label="Reliability" value={DASHBOARD.reliability + "/100"} />
                                <Stat label="Region" value="EU-WEST-2" />
                                <Stat label="PoS" value={DASHBOARD.proofStatus} icon={FileCheck2} accent="emerald" small />
                            </div>

                            <div className="grid sm:grid-cols-2 gap-3">
                                <div className="surface rounded-xl p-5">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">Lifetime Earnings</div>
                                    <div className="mt-2 flex items-baseline gap-2">
                                        <span className="font-heading font-black text-3xl text-ink">{DASHBOARD.earnings}</span>
                                        <span className="font-mono text-xs text-faint-ink">DCX</span>
                                    </div>
                                    <div className="mt-1 font-mono text-xs text-emerald-600 dark:text-emerald-400">{DASHBOARD.earnings24h} · 24h</div>
                                </div>
                                <div className="surface rounded-xl p-5">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">Active Stake</div>
                                    <div className="mt-2 flex items-baseline gap-2">
                                        <span className="font-heading font-black text-3xl text-ink">{DASHBOARD.stake}</span>
                                        <span className="font-mono text-xs text-faint-ink">DCX</span>
                                    </div>
                                    <div className="mt-2 h-1.5 rounded-full bg-card-muted overflow-hidden">
                                        <div className="h-full w-[78%] brand-gradient-bg" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-5 p-6 md:p-8">
                            <div className="flex items-center justify-between mb-4">
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">Workload History · Live</div>
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            </div>

                            <div className="space-y-2">
                                {WORKLOAD_HISTORY.map((w, i) => (
                                    <div key={i} className="surface rounded-lg px-3 py-2.5 hover:bg-card-muted transition-colors" data-testid={`workload-${i}`}>
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="font-mono text-[10px] text-faint-ink shrink-0">{w.t}</span>
                                                <span className="font-mono text-[10px] uppercase tracking-wider text-brand-blue shrink-0">{w.type}</span>
                                            </div>
                                            <span className="font-mono text-xs text-emerald-600 dark:text-emerald-400 shrink-0">{w.earn} DCX</span>
                                        </div>
                                        <div className="flex items-center justify-between mt-1.5">
                                            <span className="font-mono text-[10px] text-muted-ink truncate">{w.model}</span>
                                            <span className="font-mono text-[10px] text-faint-ink shrink-0 ml-2">{w.units}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4 pt-4 border-t border-line font-mono text-[9px] uppercase tracking-[0.2em] text-faint-ink text-right">
                                Figures illustrative · operator preview
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function Stat({ label, value, accent, icon: Icon, small }) {
    const color =
        accent === "blue" ? "text-brand-blue" :
        accent === "violet" ? "text-brand-violet" :
        accent === "emerald" ? "text-emerald-600 dark:text-emerald-400" : "text-ink";
    return (
        <div className="surface rounded-xl p-4">
            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">
                {Icon && <Icon size={11} strokeWidth={1.5} />}
                {label}
            </div>
            <div className={`mt-1.5 font-mono ${small ? "text-xs" : "text-lg"} font-semibold ${color}`}>{value}</div>
        </div>
    );
}
